/**
 * ChartDetailModal
 *
 * 차트 클릭 시 상세 거래 내역을 표시하는 모달
 * - 카테고리별 or 기간별 거래 목록
 * - 총액 및 건수 요약
 * - TransactionList 재사용
 */

import { Modal } from '@/shared/components/ui/Modal'
import { TransactionList } from '../TransactionList'
import type { ChartDetailData } from '../../types/Statistics'
import type { PaymentMethod } from '@/domains/payment/types/PaymentMethod'
import type { User } from '@/domains/user/types/User'
import type { Category } from '@/domains/category/types/Category'

interface ChartDetailModalProps {
  data: ChartDetailData | null
  paymentMethods: PaymentMethod[]
  users: User[]
  categories: Category[]
  onClose: () => void
}

export function ChartDetailModal({ data, paymentMethods, users, categories, onClose }: ChartDetailModalProps) {
  if (!data) return null

  return (
    <Modal
      isOpen={!!data}
      onClose={onClose}
      title={data.title}
      size="lg"
      showCloseButton={true}
      closeOnOverlayClick={true}
      closeOnEsc={true}
    >
      {/* 요약 섹션 */}
      <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
        {data.subtitle && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {data.subtitle}
          </p>
        )}

        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              총 {data.transactions.length}건
            </span>
          </div>
          <div className="text-xl font-bold text-expense-fixed">
            {data.totalAmount.toLocaleString()}원
          </div>
        </div>
      </div>

      {/* 거래 목록 */}
      {data.transactions.length > 0 ? (
        <div className="max-h-96 overflow-y-auto -mx-6">
          <TransactionList
            transactions={data.transactions}
            paymentMethods={paymentMethods}
            users={users}
            categories={categories}
            onTransactionLongPress={(id: string) => {
              // 통계 페이지에서는 거래 클릭 시 아무 동작 안 함
              // 필요하다면 거래 상세 페이지로 이동하는 로직 추가 가능
              console.log('Transaction clicked:', id)
            }}
            inModal={true}
          />
        </div>
      ) : (
        <div className="py-8 text-center text-gray-500 dark:text-gray-400">
          거래 내역이 없습니다
        </div>
      )}

      {/* 닫기 버튼 */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          닫기
        </button>
      </div>
    </Modal>
  )
}
